import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Feather, LogOut, Mail, WifiOff, RefreshCw } from 'lucide-react';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { useGetSessionQuery, useSignOutMutation, authApi } from './features/auth/api';
import { useMeQuery, profilesApi } from './features/profiles/api';
import { relationshipsApi } from './features/relationships/api';
import { lettersApi } from './features/letters/api';
import { notificationsApi } from './features/notifications/api';
import { AuthScreen } from './features/auth/AuthScreen';
import { ResetPasswordScreen } from './features/auth/ResetPasswordScreen';
import { parseResetToken } from './features/auth/resetPassword';
import { HandleScreen } from './features/profiles/HandleScreen';
import { FirstRun } from './features/onboarding/FirstRun';
import { HeaderMenu } from './features/onboarding/AboutToiyp';
import { QuietNotices } from './features/onboarding/QuietNotices';
import { ProfileScreen } from './features/profiles/ProfileScreen';
import { Avatar } from './features/profiles/avatar';
import { InviteScreen } from './features/mail/InviteScreen';
import { InviteLinkNote } from './features/inviteLinks/InviteLinkNote';
import { inviteLinksApi } from './features/inviteLinks/api';
import { takeInviteLinkFromUrl } from './features/inviteLinks/pending';
import { useOpenInviteLink } from './features/inviteLinks/useOpenInviteLink';
import { Messages } from './features/letters/Messenger';
import { useOfflineSync } from './features/offline/useOfflineSync';
import { useIsWide } from './useIsWide';
import { useLiveUpdates } from './features/live/useLiveUpdates';
import { clearSealQueue, flushPendingSeals } from './features/offline/sealQueueSlice';
import { wipeDrafts } from './features/drafts/draftsSlice';
import { locked } from './features/hidden/hiddenSlice';
import { NotificationBell } from './features/notifications/NotificationBell';
import { usePushNotifications } from './features/notifications/usePushNotifications';
import { useDraftReminders } from './features/notifications/useDraftReminders';
import { posthog } from './lib/posthog';
import { adminApi, useAdminMeQuery } from './features/admin/api';
import { AdminScreen } from './features/admin/AdminScreen';
import { PostageNoticeHost } from './features/postage/Postage';
import { STAMP_BOOK_HASH, StampBookScreen } from './features/postage/StampBook';
import { PROFILE_HASH } from './features/letters/route';
import { clearReadiedClasses } from './features/postage/intent';

const ADMIN_HASH = '#/admin';

function Splash() {
  return (
    <main className="app splash">
      <div className="splash__icon">
        <Feather size={26} />
      </div>
      <h1 className="splash__name">toiyp</h1>
      <p className="splash__note">checking the post…</p>
    </main>
  );
}

function MainApp({
  handle,
  inviteLink,
  openRelationshipId = null,
}: {
  handle: string;
  inviteLink: string | null;
  /** A conversation the first-run tour started — opened once, as the app arrives. */
  openRelationshipId?: string | null;
}) {
  useOfflineSync();
  useLiveUpdates(true);
  // Server-side reminders for drafts left unsealed (the draft itself never
  // leaves IndexedDB; this only tells the server one exists).
  const { cancelAll: cancelDraftReminders } = useDraftReminders();
  const dispatch = useAppDispatch();
  const [signOut] = useSignOutMutation();
  const { disable: disablePush } = usePushNotifications();
  const { data: me } = useMeQuery();
  const [editingProfile, setEditingProfile] = useState(() => window.location.hash === PROFILE_HASH);
  const wide = useIsWide();
  const [inviteLinkNotice, dismissInviteLinkNotice] = useOpenInviteLink(inviteLink);
  // After mount, like the invite link: the messenger restores its route on
  // mount, so a hash written before then is overwritten.
  useEffect(() => {
    if (openRelationshipId) window.location.hash = `#/c/${openRelationshipId}`;
  }, [openRelationshipId]);
  const { data: adminMe } = useAdminMeQuery();
  const isAdmin = adminMe?.isAdmin === true;
  // The dashboard sits on `#/admin` so it can be bookmarked and survives a reload.
  const [adminOpen, setAdminOpen] = useState(() => window.location.hash === ADMIN_HASH);
  // The stamp book sits on `#/stamps`, like the dashboard.
  const [stampsOpen, setStampsOpen] = useState(() => window.location.hash === STAMP_BOOK_HASH);
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash;
      setAdminOpen(hash === ADMIN_HASH);
      setStampsOpen(hash === STAMP_BOOK_HASH);
      // `#/profile` is a screen of its own (a Post Office letter may point at
      // it); every other hash leaves the profile behind.
      setEditingProfile(hash === PROFILE_HASH);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const openAdmin = useCallback(() => {
    window.location.hash = ADMIN_HASH;
  }, []);
  const closeStamps = useCallback(() => {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    setStampsOpen(false);
  }, []);
  const closeAdmin = useCallback(() => {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    setAdminOpen(false);
  }, []);

  const onProfile = editingProfile && !adminOpen && !stampsOpen;
  const onLetters = !editingProfile && !adminOpen && !stampsOpen;
  const closeProfile = useCallback(() => {
    setEditingProfile(false);
    if (window.location.hash === PROFILE_HASH) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);
  const goToLetters = useCallback(() => {
    closeProfile();
    if (stampsOpen) closeStamps();
    if (adminOpen) closeAdmin();
  }, [closeProfile, stampsOpen, adminOpen, closeStamps, closeAdmin]);

  const pending = useAppSelector((s) => s.offline.pending.length);
  const flushing = useAppSelector((s) => s.offline.flushing);

  const onSignOut = useCallback(async () => {
    // Clean up server state while the session still exists — the endpoints are
    // guarded. A shared device must not keep receiving the previous member's
    // letters, nor nag them about a draft that no longer exists.
    await cancelDraftReminders();
    await disablePush();
    void dispatch(wipeDrafts());
    clearReadiedClasses();
    void dispatch(clearSealQueue());
    try {
      await signOut().unwrap();
    } finally {
      posthog.reset();
      dispatch(locked());
      dispatch(authApi.util.resetApiState());
      dispatch(profilesApi.util.resetApiState());
      dispatch(relationshipsApi.util.resetApiState());
      dispatch(lettersApi.util.resetApiState());
      dispatch(notificationsApi.util.resetApiState());
      dispatch(adminApi.util.resetApiState());
      dispatch(inviteLinksApi.util.resetApiState());
      navigator.serviceWorker?.controller?.postMessage({ type: 'sign-out' });
    }
  }, [dispatch, signOut, disablePush, cancelDraftReminders]);

  return (
    <main className={`app${wide ? ' app--wide' : ''}`}>
      <header className="app-header">
        <div className="app-header__brand">
          <div className="app-header__icon">
            <Feather size={20} />
          </div>
          <div>
            <span className="app-header__name">toiyp</span>
            <span className="app-header__tagline">letters, slowly</span>
          </div>
        </div>
        {wide && (
          // Wide screens: the two top-level places live in the header, where a
          // desktop looks for them, instead of the floating pill.
          <nav className="app-header__nav" aria-label="Main">
            <button
              type="button"
              className={`app-header__tab${onLetters ? ' app-header__tab--on' : ''}`}
              aria-current={onLetters ? 'page' : undefined}
              onClick={goToLetters}
            >
              <Mail size={16} aria-hidden="true" />
              Letters
            </button>
            <button
              type="button"
              className={`app-header__tab app-header__tab--profile${onProfile ? ' app-header__tab--on' : ''}`}
              aria-current={onProfile ? 'page' : undefined}
              onClick={() => setEditingProfile(true)}
              title="Your profile"
            >
              <span className="app-header__avatar">
                <Avatar name={me?.displayName ?? handle} avatar={me?.avatar ?? null} />
              </span>
              @{handle || 'you'}
            </button>
          </nav>
        )}
        <div className="app-header__user">
          {/* Phones: admin and sign-out live in the ⋯ menu so everything fits. */}
          {isAdmin && wide && (
            <button
              type="button"
              className="icon-btn"
              onClick={openAdmin}
              aria-label="Admin dashboard"
              title="Admin dashboard"
            >
              <BarChart3 size={16} />
            </button>
          )}
          <NotificationBell />
          {!wide && (
            <button
              type="button"
              className="app-header__profile"
              onClick={() => setEditingProfile(true)}
              title="Edit your profile"
            >
              <span className="app-header__avatar">
                <Avatar name={me?.displayName ?? handle} avatar={me?.avatar ?? null} />
              </span>
              <span className="app-header__handle">@{handle || 'you'}</span>
            </button>
          )}
          <HeaderMenu
            onAdmin={!wide && isAdmin ? openAdmin : undefined}
            onSignOut={wide ? undefined : () => void onSignOut()}
          />
          {wide && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => void onSignOut()}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </header>

      <PostageNoticeHost />
      <QuietNotices />
      {inviteLinkNotice && (
        <div className="offline-banner" role="status">
          <span>{inviteLinkNotice}</span>
          <button type="button" className="linkish" onClick={dismissInviteLinkNotice}>
            OK
          </button>
        </div>
      )}
      {pending > 0 && (
        <div className="offline-banner" role="status">
          <WifiOff size={15} aria-hidden="true" />
          <span>
            {pending} letter{pending === 1 ? '' : 's'} queued while offline.
          </span>
          <button
            type="button"
            className="linkish"
            disabled={flushing}
            onClick={() => void dispatch(flushPendingSeals())}
          >
            {flushing ? (
              <>
                <RefreshCw size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                sending…
              </>
            ) : (
              'Try now'
            )}
          </button>
        </div>
      )}

      {adminOpen && isAdmin ? (
        <AdminScreen onBack={closeAdmin} />
      ) : stampsOpen ? (
        <StampBookScreen onBack={closeStamps} />
      ) : wide ? (
        // Wide screens keep the list in view; the profile opens in the side panel.
        <Messages
          onOpenProfile={() => setEditingProfile(true)}
          profileOpen={editingProfile}
          onCloseProfile={closeProfile}
        />
      ) : editingProfile ? (
        <ProfileScreen onBack={closeProfile} />
      ) : (
        <Messages onOpenProfile={() => setEditingProfile(true)} />
      )}
    </main>
  );
}

export function App() {
  const { data: sessionUser, isLoading } = useGetSessionQuery();
  const meQuery = useMeQuery(undefined, { skip: !sessionUser });
  const me = useMemo(() => meQuery.data ?? null, [meQuery.data]);

  useEffect(() => {
    posthog.capture('$pageview');
  }, []);

  useEffect(() => {
    if (sessionUser) posthog.identify(sessionUser.id);
  }, [sessionUser]);

  // A mail invite: the invitee is not signed in yet, so the invite screen
  // replaces the auth gate (it creates the account and signs them in).
  const invite = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    const email = params.get('email');
    return token && email ? { token, email } : null;
  }, []);

  // An invite link (`/?link=<token>`): kept until the signed-in app opens it,
  // so it survives creating an account and claiming a handle.
  const inviteLink = useMemo(() => takeInviteLinkFromUrl(), []);
  const [startedInTour, setStartedInTour] = useState<string | null>(null);

  // A password-reset link arrives on the hash (`#/reset-password?token=…`);
  // it must work signed-out, so it also precedes the auth gate.
  const resetToken = useMemo(() => parseResetToken(window.location.hash), []);
  if (resetToken) {
    return (
      <ResetPasswordScreen
        onDone={() => {
          window.location.hash = '';
        }}
      />
    );
  }

  if (!sessionUser && invite) {
    return <InviteScreen token={invite.token} email={invite.email} onJoined={setStartedInTour} />;
  }

  if (isLoading || (sessionUser && meQuery.isLoading)) return <Splash />;
  if (!sessionUser) {
    return inviteLink ? (
      <AuthScreen initialMode="signup" intro={<InviteLinkNote token={inviteLink} />} />
    ) : (
      // The landing page's "Get your toiyp" asks for the sign-up tab; "Log in" (and everything else) opens sign-in.
      <AuthScreen initialMode={new URLSearchParams(window.location.search).get('mode') === 'signup' ? 'signup' : 'signin'} />
    );
  }
  if (me?.handle == null) return <HandleScreen inviteLink={inviteLink} />;
  // Once per account, straight after the handle: the first-run tour.
  if (me.onboardingPending) {
    return <FirstRun viaInviteLink={inviteLink !== null} onStartedConversation={setStartedInTour} />;
  }
  return <MainApp handle={me.handle} inviteLink={inviteLink} openRelationshipId={startedInTour} />;
}
